/**
 * STEP 2: CLIENT DISCOVERY
 * 
 * Problem, budget, timeline, living situation
 * 
 * Fields:
 * - Problem description (written narrative)
 * - Why now (trigger)
 * - Success definition
 * - Budget range and flexibility
 * - Timeline (desired start, urgency, hard deadline)
 * - Living situation (staying, occupants, pets, WFH)
 * 
 * Theme: Spec Ops
 */

import React from 'react';
import StepContainer from './StepContainer';

const BUDGET_RANGES = [
  { value: 'under_5k', label: 'Under $5,000' },
  { value: '5k_10k', label: '$5,000 - $10,000' },
  { value: '10k_25k', label: '$10,000 - $25,000' },
  { value: '25k_50k', label: '$25,000 - $50,000' },
  { value: '50k_100k', label: '$50,000 - $100,000' },
  { value: '100k_plus', label: '$100,000+' },
  { value: 'unknown', label: "Don't know / Won't say" },
];

const Step2ClientDiscovery = ({ 
  data = {},
  onUpdate,
  onSave,
  onPrev,
  onNext,
  saving = false,
  isComplete = false,
  validationErrors = [],
}) => {
  const updateField = (key, value) => {
    if (isComplete) return;
    onUpdate?.('client_discovery', { ...data, [key]: value });
  };

  return (
    <StepContainer
      step={2}
      onPrev={onPrev}
      onNext={onNext}
      onSave={onSave}
      isSaving={saving}
      isComplete={isComplete}
      validationErrors={validationErrors}
    >
      <div className="space-y-6 max-w-4xl">
        {/* The Problem */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            The Problem
          </h3>
          
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              What problem are they trying to solve? <span className="text-red-400">*</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">Use their exact words when possible</p>
            <textarea
              value={data.problem_description || ''}
              onChange={(e) => updateField('problem_description', e.target.value)}
              disabled={isComplete}
              rows={4}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
              placeholder="The bathroom is outdated, the tub leaks, and they want to convert to a walk-in shower..."
            />
          </div>
          
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              Why now? <span className="text-red-400">*</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">What triggered this project?</p>
            <textarea
              value={data.why_now || ''}
              onChange={(e) => updateField('why_now', e.target.value)}
              disabled={isComplete}
              rows={3}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
              placeholder="Planning to sell in spring, just had a water damage incident, aging parent moving in..."
            />
          </div>
          
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              What does success look like? <span className="text-red-400">*</span>
            </label>
            <p className="text-xs text-slate-500 mb-2">How will they know the project was successful?</p>
            <textarea
              value={data.success_definition || ''}
              onChange={(e) => updateField('success_definition', e.target.value)}
              disabled={isComplete}
              rows={3}
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
              placeholder="A spa-like bathroom they can enjoy, everything done before the in-laws visit..."
            />
          </div>
        </div>

        {/* Budget */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Budget
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Budget Range <span className="text-red-400">*</span>
              </label>
              <select
                value={data.budget_range || ''}
                onChange={(e) => updateField('budget_range', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select range...</option>
                {BUDGET_RANGES.map(range => (
                  <option key={range.value} value={range.value}>{range.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Budget Flexibility
              </label>
              <select
                value={data.budget_flexibility || ''}
                onChange={(e) => updateField('budget_flexibility', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="firm">Firm - cannot exceed</option>
                <option value="some_flex">Some flexibility for the right solution</option>
                <option value="flexible">Flexible - quality is priority</option>
                <option value="unknown">Unknown</option>
              </select>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Timeline
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Desired Start <span className="text-red-400">*</span>
              </label>
              <select
                value={data.timeline_desired_start || ''}
                onChange={(e) => updateField('timeline_desired_start', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="asap">ASAP</option>
                <option value="1_2_weeks">1-2 weeks</option>
                <option value="1_month">Within a month</option>
                <option value="2_3_months">2-3 months</option>
                <option value="3_plus_months">3+ months</option>
                <option value="flexible">Flexible / No rush</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Urgency Level
              </label>
              <select
                value={data.timeline_urgency || ''}
                onChange={(e) => updateField('timeline_urgency', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="emergency">Emergency - issue causing damage</option>
                <option value="urgent">Urgent - need done quickly</option>
                <option value="normal">Normal - standard timeline</option>
                <option value="low">Low - whenever convenient</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Hard Deadline?
              </label>
              <select
                value={data.timeline_hard_deadline || ''}
                onChange={(e) => updateField('timeline_hard_deadline', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="yes">Yes - must be done by date</option>
                <option value="preferred">Preferred but flexible</option>
                <option value="no">No hard deadline</option>
              </select>
            </div>
          </div>
          
          {(data.timeline_hard_deadline === 'yes' || data.timeline_hard_deadline === 'preferred') && (
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Deadline Date & Reason
              </label>
              <input
                type="text"
                value={data.timeline_deadline_reason || ''}
                onChange={(e) => updateField('timeline_deadline_reason', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                placeholder="e.g., April 15 - in-laws arriving, June 1 - house going on market"
              />
            </div>
          )}
        </div>

        {/* Living Situation */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Living Situation
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Will they stay during work?
              </label>
              <select
                value={data.staying_during_work || ''}
                onChange={(e) => updateField('staying_during_work', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="yes">Yes - staying on site</option>
                <option value="partial">Partial - some nights elsewhere</option>
                <option value="no">No - moving out during work</option>
                <option value="vacant">Property is vacant</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Work from home?
              </label>
              <select
                value={data.work_from_home || ''}
                onChange={(e) => updateField('work_from_home', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="yes_always">Yes - full time</option>
                <option value="yes_partial">Yes - part time/hybrid</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Adults in home
              </label>
              <input
                type="number"
                min="0"
                value={data.occupants_adults || ''}
                onChange={(e) => updateField('occupants_adults', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                placeholder="0"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Children in home
              </label>
              <input
                type="number"
                min="0"
                value={data.occupants_children || ''}
                onChange={(e) => updateField('occupants_children', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                placeholder="0"
              />
            </div>
            
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Pets?
              </label>
              <select
                value={data.pets || ''}
                onChange={(e) => updateField('pets', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
              >
                <option value="">Select...</option>
                <option value="none">No pets</option>
                <option value="dog">Dog(s)</option>
                <option value="cat">Cat(s)</option>
                <option value="both">Dogs and cats</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          
          {data.pets && data.pets !== 'none' && (
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Pet Details (names, concerns)
              </label>
              <input
                type="text"
                value={data.pets_details || ''}
                onChange={(e) => updateField('pets_details', e.target.value)}
                disabled={isComplete}
                className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
                  focus:border-emerald-500 focus:outline-none disabled:opacity-50"
                placeholder="e.g., Max (dog) - keep gates closed, Fluffy - nervous around strangers"
              />
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="space-y-4">
          <h3 className="text-emerald-400 font-semibold uppercase tracking-wider text-sm border-b border-slate-700 pb-2">
            Additional Notes
          </h3>
          <textarea
            value={data.notes || ''}
            onChange={(e) => updateField('notes', e.target.value)}
            disabled={isComplete}
            rows={3}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
              focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
            placeholder="Any other discovery notes..."
          />
        </div>
      </div>
    </StepContainer>
  );
};

export default Step2ClientDiscovery;
